import React from 'react';

import { InjectedIntl, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import InfoIcon from '@atlaskit/icon/glyph/info';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { LoadingStatus } from 'src/constants/loading-status';
import { updateIssueTransition } from 'src/redux/jira/actions';
import { PullRequestJiraIssuesState } from 'src/redux/jira/reducers/pull-request-jira-issues';
import { getPullRequestJiraIssuesState } from 'src/redux/jira/selectors/jira-issue-selectors';
import { PrCommentJiraIssue } from 'src/redux/jira/types';
import { FakeLabel } from 'src/styles';
import { BucketState } from 'src/types/state';

import AutomaticIssueTransitionFormRow from '../issue-transition-form-row/automatic-issue-transition-form-row';
import IssueTransitionFormRow from '../issue-transition-form-row/issue-transition-form-row';

import IssueTransitionErrorForm from './issue-transition-error-form';
import messages from './issue-transition-form.i18n';
import * as styles from './issue-transition-form.style';

type IssueTransitionFormProps = {
  intl: InjectedIntl;
  pullRequestJiraIssues: PrCommentJiraIssue[];
};

/* eslint @typescript-eslint/ban-types: "warn" */
const IssueTransitionForm: React.FC<IssueTransitionFormProps> = ({
  intl,
  pullRequestJiraIssues,
}) => {
  const pullRequestJiraIssuesState = useSelector<
    BucketState,
    PullRequestJiraIssuesState
  >(getPullRequestJiraIssuesState);
  const dispatch = useDispatch();

  const { issueTransitionFormData } = pullRequestJiraIssuesState;

  if (pullRequestJiraIssues.length === 0) {
    return null;
  }

  const isLoadingOrError =
    issueTransitionFormData.filter(
      row =>
        row.availableIssueTransitionsFetchedStatus !== LoadingStatus.Success
    ).length > 0;

  if (isLoadingOrError) {
    return <IssueTransitionErrorForm />;
  }

  const automaticTransitions = issueTransitionFormData.filter(
    row => row.automaticTransition
  );

  if (automaticTransitions.length > 0) {
    return (
      <>
        <styles.HeadingContainer>
          <FakeLabel>
            {intl.formatMessage(messages.workflowTransitionIssues, {
              issueCount: automaticTransitions.length,
            })}
          </FakeLabel>
          <styles.InfoContainer>
            <Tooltip
              content={intl.formatMessage(messages.workflowTransitionInfo, {
                issueCount: automaticTransitions.length,
              })}
            >
              <InfoIcon
                label={intl.formatMessage(messages.workflowTransitionInfo, {
                  issueCount: automaticTransitions.length,
                })}
                primaryColor={token('color.icon', colors.N300)}
                size="small"
              />
            </Tooltip>
          </styles.InfoContainer>
        </styles.HeadingContainer>
        {automaticTransitions.map((row, index) => (
          <AutomaticIssueTransitionFormRow
            key={index}
            issueTransitionFormRow={row}
          />
        ))}
      </>
    );
  } else {
    const hasUnsupportedTransitions =
      issueTransitionFormData.filter(row => row.hasUnsupportedTransitions)
        .length > 0;

    return (
      <>
        <FakeLabel>
          {intl.formatMessage(messages.transitionIssues, {
            issueCount: pullRequestJiraIssues.length,
          })}
        </FakeLabel>
        {issueTransitionFormData.map((row, index) => (
          <IssueTransitionFormRow
            key={index}
            issueTransitionFormRow={row}
            updateIssueTransition={newTransition =>
              dispatch(updateIssueTransition({ index, newTransition }))
            }
          />
        ))}
        {(pullRequestJiraIssues.length > 3 || hasUnsupportedTransitions) && (
          <styles.LineContainer>
            <styles.AdditionalTransitions>
              {intl.formatMessage(messages.transitionAdditional)}
            </styles.AdditionalTransitions>
          </styles.LineContainer>
        )}
      </>
    );
  }
};

export default injectIntl(IssueTransitionForm);
